import { type Nullable } from '@Types';
import moment from 'moment-timezone';
import { type Cohort, COHORT_SECTIONS, getRegistrationDeadline as getRegistrationDeadlineForCohort } from 'Cohorts';
import { statsigClientProvider } from 'StatsigClientProvider';
import { type AdmissionOffer, AdmissionOfferStatus } from './AdmissionOffer.types';

export const offeredAdmission = (admissionOffer: Nullable<AdmissionOffer>) =>
    admissionOffer?.status === AdmissionOfferStatus.OfferedAdmission;

export const acceptedAdmissionOffer = (admissionOffer: Nullable<AdmissionOffer>) =>
    admissionOffer?.status === AdmissionOfferStatus.AcceptedAdmissionOffer;

export function declinedAdmissionOffer(admissionOffer: Nullable<AdmissionOffer>) {
    return admissionOffer?.status === AdmissionOfferStatus.DeclinedAdmissionOffer;
}

export function earlyRegistrationDeadlineMs(admissionOffer: Nullable<AdmissionOffer>): Nullable<number> {
    return earlyRegistrationDeadlineMoment(admissionOffer)?.valueOf() || null;
}

function earlyRegistrationDeadlineMoment(admissionOffer: Nullable<AdmissionOffer>): Nullable<moment.Moment> {
    if (!admissionOffer || !admissionOffer.earlyRegistrationDeadline) return null;

    return moment(1000 * admissionOffer.earlyRegistrationDeadline).startOf('day');
}

export function inEarlyAdmissionsRegistrationPeriod(admissionOffer: AdmissionOffer) {
    if (!admissionOffer) return false;

    const earlyAdmissionRegistrationDeadline = earlyRegistrationDeadlineMs(admissionOffer);
    return !!earlyAdmissionRegistrationDeadline && Date.now() < earlyAdmissionRegistrationDeadline;
}

function customRegistrationDeadlineFromReportsTags(reportsTags: string[]): Date | null {
    if (!reportsTags.length) return null;

    const config = statsigClientProvider.client?.getDynamicConfig('custom_registration_deadline_by_reports_tag');
    const deadlinesByTag = config?.value as Record<string, string> | undefined;
    if (!deadlinesByTag) return null;

    for (const tag of reportsTags) {
        const dateStr = deadlinesByTag[tag];
        if (dateStr) {
            // Midnight in the browser's local timezone, combined with shiftMonthDayForThreshold in
            // the user-facing date formatter, means "2026-07-01" displays in the UI as Jun 30, 2026.
            const deadline = moment(dateStr).startOf('day').toDate();
            if (Date.now() < deadline.getTime()) return deadline;
        }
    }

    return null;
}

export function getRegistrationDeadline(admissionOffer: AdmissionOffer, cohort: Cohort): Date | null {
    if (!admissionOffer) return null;

    const customDeadline = customRegistrationDeadlineFromReportsTags(admissionOffer.reportsTags);
    if (customDeadline) return customDeadline;

    if (inEarlyAdmissionsRegistrationPeriod(admissionOffer)) {
        const asMoment = earlyRegistrationDeadlineMoment(admissionOffer);
        if (!asMoment) {
            throw new Error(
                'How could earlyRegistrationDeadlineMoment return null when inEarlyAdmissionsRegistrationPeriod is true?',
            );
        }
        return asMoment.toDate();
    }

    return getRegistrationDeadlineForCohort(cohort);
}

export function getGlobalCohortSectionOffer(admissionOffer: AdmissionOffer) {
    return admissionOffer.cohortSectionOffers.find(cso => cso.cohortSectionId === COHORT_SECTIONS.GLOBAL.id);
}
